import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react'
import { VisuallyHidden } from '@red-ui/components'

/*
  If the live region is conditionally rendered or added to the DOM dynamically, 
  some screen readers (ex. Voice Over) may not recognize it.
  This component centralizes the logic for updating live announcements 
  and ensures that the live region exists from the initial render.
*/

type AriaLive = 'polite' | 'assertive'

type LiveAnnouncerContextType = {
  announce: (message: string, liveType?: AriaLive) => void
}

const LiveAnnouncerContext = createContext<LiveAnnouncerContextType | undefined>(undefined)

export const A11yLiveAnnouncerProvider = ({ children }: { children: ReactNode }) => {
  const [liveMessage, setLiveMessage] = useState<string>('')
  const [ariaLive, setAriaLive] = useState<AriaLive>('assertive')

  const announce = useCallback((message: string, liveType: AriaLive = 'assertive') => {
    setLiveMessage('') // Clear the message first to trigger screen readers
    setAriaLive(liveType)
    setTimeout(() => {
      // Set the new message after a short delay
      setLiveMessage(message)
    }, 50) // Ensure DOM updates are detected
  }, [])

  const value = useMemo(() => ({ announce }), [announce])

  return (
    <LiveAnnouncerContext.Provider value={value}>
      <VisuallyHidden aria-live={ariaLive} aria-atomic="true">
        {liveMessage}
      </VisuallyHidden>
      {children}
    </LiveAnnouncerContext.Provider>
  )
}

export const useA11yLiveAnnouncer = () => {
  const context = useContext(LiveAnnouncerContext)
  if (!context) {
    throw new Error('useLiveAnnouncer must be used within a A11yLiveAnnouncerProvider')
  }
  return context
}
